/**
 * The People list's grouping and walking, kept out of the components so
 * they can be tested without a DOM.
 */

import type { PersonCandidate } from '@satisa-memory/shared'

export type PeopleView = 'review' | 'everyone' | 'org'

/**
 * A filter of its own, not a source: invited to meetings and nothing else —
 * no link, no Slack account. The calendar invitees the sync never met.
 */
export const CALENDAR_ONLY = 'calendar_only'

const calendarOnly = (p: { sources: string[] }) =>
  p.sources.length === 1 && p.sources[0] === 'calendar'

/** What a person can be filtered by: their sources, and Calendar only. */
export const filterTags = (p: { sources: string[] }) =>
  calendarOnly(p) ? [...p.sources, CALENDAR_ONLY] : p.sources

/** A person known from `source`; any person when no source is chosen. */
export const fromSource = (p: { sources: string[] }, source: string) =>
  !source || filterTags(p).includes(source)

/** A pair is in a source when either of the two is. */
export const pairFromSource = (c: PersonCandidate, source: string) =>
  fromSource(c.keep, source) || fromSource(c.fold, source)

/**
 * How many rows each choice would leave, most first: the sidebar's choices.
 * Calendar only sits right under Calendar, which it narrows.
 */
export function sourceCounts<T>(rows: T[], tagsOf: (row: T) => string[]) {
  const counts = new Map<string, number>()
  for (const row of rows)
    for (const s of new Set(tagsOf(row))) counts.set(s, (counts.get(s) ?? 0) + 1)
  const list = [...counts]
    .filter(([source]) => source !== CALENDAR_ONLY)
    .map(([source, count]) => ({ source, count }))
    .sort((a, b) => b.count - a.count || a.source.localeCompare(b.source))
  const only = counts.get(CALENDAR_ONLY)
  if (only) {
    const at = list.findIndex((s) => s.source === 'calendar')
    list.splice(at + 1, 0, { source: CALENDAR_ONLY, count: only })
  }
  return list
}

export interface PairGroup {
  key: string
  name: string
  items: PersonCandidate[]
}

/**
 * By strength: one identifier or the same name settles most pairs at a
 * glance; a bare first name needs the links beside it.
 */
const BANDS = [
  { key: 'likely', name: 'Likely the same', min: 80 },
  { key: 'maybe', name: 'Worth a look', min: 50 },
  { key: 'first', name: 'First name only', min: 0 }
] as const

export function pairGroups(candidates: PersonCandidate[]): PairGroup[] {
  return BANDS.map((b, i) => ({
    key: b.key,
    name: b.name,
    items: candidates.filter((c) => c.score >= b.min && (i === 0 || c.score < BANDS[i - 1]!.min))
  })).filter((g) => g.items.length > 0)
}

/**
 * Where to go after answering the pair at `key`: the next pair in the list
 * as it was, if it is still there once the queue is read again; else the
 * first one left; else nowhere.
 */
export function nextPairAfter(before: string[], key: string, after: string[]): string | null {
  const left = new Set(after)
  const i = before.indexOf(key)
  for (const k of [...before.slice(i + 1), ...before.slice(0, Math.max(i, 0))]) {
    if (k !== key && left.has(k)) return k
  }
  return after.find((k) => k !== key) ?? null
}

const EMAIL = /[^\s<>(),;]+@[^\s<>(),;]+\.[^\s<>(),;]+/

/**
 * Someone not yet known, as typed into a person picker: a name, an address,
 * or both ("Kalina Petrova <kalina@visma.com>"). Null when there is too
 * little to go on.
 */
export function newPersonFrom(query: string): { name?: string; email?: string } | null {
  const email = query.match(EMAIL)?.[0].toLowerCase()
  const name = query
    .replace(EMAIL, ' ')
    .replace(/[<>(),;]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 120)
  if (!email && name.length < 2) return null
  return { ...(name ? { name } : {}), ...(email ? { email } : {}) }
}

/** How a new person reads in the picker's offer. */
export const newPersonLabel = (p: { name?: string; email?: string }) =>
  p.name && p.email ? `${p.name} (${p.email})` : (p.name ?? p.email ?? '')
